/** @format */

import { CommonModule, DOCUMENT, isPlatformServer } from '@angular/common';
import { Component, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Chain, Environment } from '@shared/data-access/interfaces';
import { filter, map, switchMap } from 'rxjs/operators';
import { CeopsService } from '../services/ceops.service';
import { UtilityService } from '@shared/data-access/services';

@Component({
	selector: 'shared-ceops',
	imports: [CommonModule],
	templateUrl: './ceops.component.html',
	styleUrl: './ceops.component.scss',
	standalone: true
})
export class CeopsComponent implements OnInit {
	/**
	 * @description
	 * Feature flag to enable server-side caching of the CEOPS script using Transfer State.
	 */
	useCachedCeopsScripts = false;

	socialCommerceFlow = false;

	constructor(
		private ceopsService: CeopsService,
		public chain: Chain,
		private router: Router,
		private readonly environment: Environment,
		private readonly utilityService: UtilityService,
		@Inject(DOCUMENT) private readonly document: Document,
		@Inject(PLATFORM_ID) private readonly platformId
	) {}

	ngOnInit(): void {
		this.useCachedCeopsScripts = this.utilityService.checkKillSwitch(
			'useCachedCeopsScripts',
			this.environment.killSwitch,
			this.chain.chainIdentifierAbbr
		);

		this.socialCommerceFlow = this.utilityService.checkKillSwitch(
			'socialCommerceFlow',
			this.environment.killSwitch,
			this.chain.chainIdentifierAbbr
		);

		this.router.events
			.pipe(
				filter((event) => event instanceof NavigationEnd),
				map((route: any) => route.url.split('?')[0]),
				switchMap((url) =>
					this.ceopsService.getScripts$(
						url,
						this.chain.chainMarketingIdentifierAbbr.toUpperCase(),
						3,
						this.useCachedCeopsScripts
					)
				)
			)
			.subscribe((response) => {
				const filteredResponse = this.filterCEOPSContent(response);
				this.handleCEOPSContent(filteredResponse);
			});
	}

	public filterCEOPSContent(response) {
		if (response.scripts.length <= 0) {
			return [];
		}

		if (this.socialCommerceFlow) {
			const excludedPaths = ['/one-offs/live-chat-link', '/live-chat/index'];
			response.scripts = response.scripts.filter(
				(script) => !excludedPaths.includes(script.path)
			);
		}
		return response;
	}

	public handleCEOPSContent(response) {
		if (response.scripts.length <= 0) {
			return;
		}

		const fragment = this.document.createDocumentFragment();
		const fragmentPreloads = this.document.createDocumentFragment();

		response.scripts.forEach((script) => {
			if (!script.path) {
				return;
			}
			const id = `ceopsScript_${script.path.replace(/\//g, '__')}`;

			if (this.document.getElementById(id)) {
				return;
			}

			if (
				this.environment.isLocal ||
				(!isPlatformServer(this.platformId) && !script.loadSynchronously)
			) {
				this.createScriptTag(fragment, script, id);
			} else if (isPlatformServer(this.platformId) && script.loadSynchronously) {
				// Script tag
				this.createScriptTag(fragment, script, id);

				// Preload link tag
				const linkEl = this.document.createElement('link');
				linkEl.setAttribute('rel', 'preload');
				linkEl.setAttribute('href', script.src);
				linkEl.setAttribute('as', 'script');
				linkEl.setAttribute('fetchpriority', 'high');
				fragmentPreloads.appendChild(linkEl);
			}
		});

		if (isPlatformServer(this.platformId)) {
			this.document.head.appendChild(fragmentPreloads);
		}
		this.document.head.appendChild(fragment);
	}

	private createScriptTag(fragment: DocumentFragment, script, id: string) {
		const newEl = this.document.createElement('script');
		newEl.src = script.src;
		newEl.type = 'application/javascript';
		newEl.id = id;

		if (!script.loadSynchronously) {
			newEl.async = true;
		}

		fragment.appendChild(newEl);
	}
}
